'use client';

import type { AttachmentView } from '@components/files/AttachmentGallery';
import { MAX_FILES_PER_PARENT, type FileParentType } from '@files/constants';
import {
  prepareLocalFiles,
  revokePreview,
  uploadPendingFile,
  type FileValidationCode,
  type PendingLocalFile,
  type UploadTargetContext
} from '@files/upload-client';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { runZeroMutation } from '../../zero/run-mutation';
import { useAppZero } from '../../zero/use-app-zero';
import { useCurrentOrgUser } from '../../zero/use-current-user';

export type ParentFileUploadOptions = {
  organizationId: string;
  parentId: string;
  parentType: Extract<FileParentType, 'menus' | 'recipes' | 'ingredients'>;
  /** Files already stored on the parent; counts toward the per-parent cap. */
  existingCount: number;
  canEdit: boolean;
  onBeforeUpload?: () => Promise<boolean>;
  /** Rejects picked files (as unsupported) before they are prepared. */
  acceptFile?: (file: File) => boolean;
  /** Called with each file id once its `files` row is saved. */
  onUploaded?: (fileId: string) => void;
};

/**
 * Pick → upload to blob storage → persist the `files` row, with retry for
 * uploads whose row could not be saved. Shared by the generic attachment
 * list and the menu cover photo card.
 */
export function useParentFileUpload({
  organizationId,
  parentId,
  parentType,
  existingCount,
  canEdit,
  onBeforeUpload,
  acceptFile,
  onUploaded
}: ParentFileUploadOptions) {
  const t = useTranslations('files');
  const z = useAppZero();
  const orgUser = useCurrentOrgUser(organizationId);
  const uploadingRef = useRef(false);
  const pendingCleanupRef = useRef<PendingLocalFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState<PendingLocalFile[]>([]);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  pendingCleanupRef.current = pending;

  useEffect(() => () => pendingCleanupRef.current.forEach(revokePreview), []);

  const target: UploadTargetContext = {
    organizationId,
    parentId,
    parentType
  };

  const persistPending = async (queue: PendingLocalFile[]) => {
    if (!canEdit || uploadingRef.current || !queue.length) {
      return;
    }
    uploadingRef.current = true;
    let remaining = [...queue];
    try {
      if (onBeforeUpload && !(await onBeforeUpload())) {
        return;
      }
      setUploading(true);
      setError(null);
      for (const candidate of queue) {
        const uploaded = await uploadPendingFile(candidate, target, value =>
          setProgress(current => ({ ...current, [candidate.id]: value }))
        );
        const retained = {
          ...candidate,
          ...uploaded,
          localFile: undefined
        };
        remaining = remaining.map(file =>
          file.id === candidate.id ? retained : file
        );
        setPending(remaining);
        const saved = await runZeroMutation(
          z.mutate.files.create({
            ...uploaded,
            organizationId,
            parentId,
            parentType,
            uploadedByUserId: orgUser?.userId,
            now: Date.now()
          }),
          { errorTitle: t('upload-error') }
        );
        if (!saved) {
          throw new Error('persist-failed');
        }
        onUploaded?.(uploaded.id);
        revokePreview(candidate);
        remaining = remaining.filter(file => file.id !== candidate.id);
        setPending(remaining);
      }
    } catch {
      setPending(remaining);
      setError(t('upload-error'));
    } finally {
      uploadingRef.current = false;
      setProgress({});
      setUploading(false);
    }
  };

  const addFiles = async (list: File[]) => {
    if (!canEdit || uploadingRef.current) {
      return;
    }
    const accepted = acceptFile ? list.filter(acceptFile) : list;
    const rejected: FileValidationCode[] =
      accepted.length < list.length ? ['unsupported-type'] : [];
    const result = await prepareLocalFiles(
      accepted,
      MAX_FILES_PER_PARENT - existingCount - pending.length
    );
    const errors = [...new Set([...rejected, ...result.errors])];
    if (errors.length) {
      setError(
        errors
          .map(code => t(`validation-${code}` as Parameters<typeof t>[0]))
          .join(' ')
      );
    }
    if (!result.files.length) {
      return;
    }
    const queue = [...pending, ...result.files];
    setPending(queue);
    await persistPending(queue);
  };

  const removePending = (id: string) => {
    setPending(current => {
      const removed = current.find(file => file.id === id);
      if (removed) {
        revokePreview(removed);
      }
      return current.filter(file => file.id !== id);
    });
    setError(null);
  };

  const removeFile = (file: Pick<AttachmentView, 'id'>) =>
    runZeroMutation(z.mutate.files.remove({ id: file.id, organizationId }), {
      errorTitle: t('remove-error')
    });

  const pendingViews: AttachmentView[] = pending.map(file => ({
    id: file.id,
    url: '',
    previewUrl: file.previewUrl,
    filename: file.filename,
    contentType: file.contentType,
    sizeBytes: file.sizeBytes,
    width: file.width,
    height: file.height
  }));

  return {
    pending,
    pendingViews,
    uploading,
    progressPercent: Math.round(Math.max(0, ...Object.values(progress), 0)),
    error,
    atLimit: existingCount + pending.length >= MAX_FILES_PER_PARENT,
    addFiles,
    retry: () => persistPending(pending),
    removePending,
    removeFile
  };
}
